import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {withStyles, TextField, Button, CircularProgress} from '@material-ui/core';
import {getProfile} from '../../../../../redux/actions/User/Profile/profileAction';
import {bookingImportSetup, bookingImportStatus, bookingImportCheck, clearBookingImport} from '../../../../../redux/actions/General/BookingImport/bookingImportActions';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import {Alert} from '@material-ui/lab';

const titleStyles = theme => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
});

const DialogTitle = withStyles(titleStyles)(props => {
    const {children, classes, onClose, ...other} = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography variant="h6">{children}</Typography>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon className="close-button"/>
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles(theme => ({
    root: {padding: theme.spacing(2)},
}))(MuiDialogContent);

const DialogActions = withStyles(theme => ({
    root: {margin: 0, padding: theme.spacing(3, 3)},
}))(MuiDialogActions);

function BookingInfoImport({
    userEmail,
    getProfile,
    bookingImportSetup,
    bookingImportStatus,
    bookingImportCheck,
    clearBookingImport,
    fetching,
    setupData,
    setupError,
    statusFetching,
    statusData,
    checkData,
    // Ayarlar sayfasi butonu daha once aktarilmis olsa da gostermek ister ("Yeniden Aktar").
    alwaysShowButton,
    buttonLabel,
    buttonClassName,
    buttonIcon,
    // Profili zaten ceken bir sayfanin icindeyken ayni uca ikinci istek gitmesin diye.
    skipProfileFetch,
}) {
    const [open, setOpen] = useState(false);
    const [bookingId, setBookingId] = useState('');
    const [validationError, setValidationError] = useState('');

    useEffect(() => {
        if (!skipProfileFetch && !userEmail && typeof getProfile === 'function') {
            getProfile();
        }
    }, [userEmail, getProfile, skipProfileFetch]);

    useEffect(() => {
        if (typeof bookingImportCheck === 'function') {
            bookingImportCheck();
        }
    }, [bookingImportCheck]);

    const alreadyImported = checkData && checkData.status === 200
        && !checkData.error && checkData.data && checkData.data.imported === true;

    const setupId = setupData && setupData.status === 200
        && setupData.data && setupData.data.setup_id;

    useEffect(() => {
        if (setupId) {
            setTimeout(() => {
                bookingImportStatus(setupId);
            }, 1000);
        }
    }, [setupId, bookingImportStatus]);

    // api'deki Booking.com import izin listesiyle ayni olmali; listede olmayan 403 alir.
    const ALLOWED_EMAILS = [
        'bulut@extranetwork.com',
        'cemile@extranetwork.com',
        'cem@extranetwork.com',
        'yoy@extranetwork.com',
        'burak@extranetwork.com'
    ];
    if (!userEmail || !ALLOWED_EMAILS.includes(userEmail.toLowerCase())) {
        return null;
    }

    const reset = () => {
        setBookingId('');
        setValidationError('');
        clearBookingImport();
    };

    const handleOpen = () => setOpen(true);

    const isBusy = fetching || statusFetching;
    const statusEnvelopeOk = statusData?.status === 200 && !statusData?.error;
    const statusValue = statusEnvelopeOk ? statusData?.data?.status : null;
    const statusApiError = !!statusData && !statusEnvelopeOk;
    const statusApiErrorMessage = statusData?.message;
    const isComplete = statusValue === 'completed' || statusValue === 'completed_with_errors';
    const isFailed = statusValue === 'failed' || statusApiError;

    const statusLabelMap = {
        pending: 'Bekliyor',
        running: 'İşleniyor',
        completed: 'Tamamlandı',
        completed_with_errors: 'Tamamlandı (uyarılarla)',
        failed: 'Başarısız',
    };
    const statusLabel = statusLabelMap[statusValue] || statusValue || 'Bilinmiyor';
    const statusColor = isComplete ? '#10b981'
        : isFailed ? '#dc2626'
        : statusValue === 'running' ? '#003580'
        : '#1f2937';

    const handleCloseAndReload = () => {
        if (isBusy) return;
        const shouldReload = isComplete && !isLikelyInvalidHotel;
        reset();
        setOpen(false);
        if (shouldReload) {
            setTimeout(() => {
                window.location.reload();
            }, 400);
        }
    };

    const handleImport = () => {
        if (!bookingId.trim()) {
            setValidationError("Lütfen geçerli bir Booking.com Hotel ID giriniz.");
            return;
        }
        setValidationError('');
        clearBookingImport();
        bookingImportSetup({booking_hotel_id: bookingId.trim()});
    };

    const isSuccess = setupData && setupData.status === 200;
    const apiErrorMessage = (setupData && setupData.error && setupData.message)
        || (setupError && setupError.response && setupError.response.data && setupError.response.data.message)
        || (setupError && 'Bir hata oluştu. Lütfen tekrar deneyin.');

    const result = isSuccess && setupData.data && setupData.data.result;
    const mode = isSuccess && setupData.data && setupData.data.mode;

    const stepKeys = result ? Object.keys(result) : [];
    const failedSteps = stepKeys.filter(k => result && result[k] && result[k].status === 'failed');
    const okSteps = stepKeys.filter(k => result && result[k] && result[k].status === 'ok');
    // warning: adim calisti ama veri gelmedi (or. Booking.com'da oda yok); final status'u bozmaz.
    const warningSteps = stepKeys.filter(k => result && result[k] && result[k].status === 'warning');
    const hasFailures = failedSteps.length > 0;
    const hasWarnings = warningSteps.length > 0;
    const detailsFailed = result && result.details && result.details.status === 'failed';
    const isLikelyInvalidHotel = detailsFailed && okSteps.length <= 1;

    const stepLabelMap = {
        details: 'Otel bilgileri',
        description: 'Açıklama',
        photos: 'Fotoğraflar',
        facilities: 'Olanaklar',
        rooms: 'Oda tipleri',
        reviews: 'Yorumlar',
    };

    const statRow = (label, value) => (
        <div style={{display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #eef0f4'}}>
            <span style={{color: '#5b6675'}}>{label}</span>
            <span style={{fontWeight: 600, color: '#1f2937'}}>{value}</span>
        </div>
    );

    return (
        <>
            {(!alreadyImported || alwaysShowButton) && (
                <Button
                    variant={buttonClassName ? 'text' : 'contained'}
                    color={buttonClassName ? 'default' : 'primary'}
                    startIcon={buttonIcon !== undefined ? buttonIcon : <CloudDownloadIcon/>}
                    onClick={handleOpen}
                    style={{
                        height: 40,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: 'none',
                    }}
                    className={buttonClassName || 'booking-import-button'}
                >
                    {buttonLabel || "Booking.com'dan İçe Aktar"}
                </Button>
            )}

            <Dialog
                onClose={handleCloseAndReload}
                open={open}
                maxWidth="sm"
                fullWidth
                disableBackdropClick={true}
                disableEscapeKeyDown={isBusy}
                aria-labelledby="booking-info-import-title"
            >
                <DialogTitle className="red-bottom-border" id="booking-info-import-title" onClose={isBusy ? undefined : handleCloseAndReload}>
                    Booking.com'dan Bilgileri İçe Aktar
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" style={{color: '#5b6675', marginBottom: 16}}>
                        Otelin Booking.com ID bilgisini giriniz. İçe aktarma işlemi sırasında otel
                        bilgileri, fotoğraflar, olanaklar ve oda tipleri otomatik olarak
                        sisteme aktarılacaktır.
                    </Typography>

                    <div style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-end',
                        flexWrap: 'wrap',
                        marginBottom: 16,
                    }}>
                        <div style={{flex: '1 1 240px', minWidth: 200}}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                label="Booking.com Hotel ID"
                                placeholder="örn: 1234567"
                                value={bookingId}
                                onChange={e => setBookingId(e.target.value)}
                                disabled={fetching}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleImport();
                                    }
                                }}
                            />
                        </div>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleImport}
                            disabled={fetching || !bookingId.trim()}
                            style={{
                                height: 40,
                                minWidth: 160,
                                textTransform: 'none',
                                fontWeight: 600,
                            }}
                        >
                            {fetching
                                ? <CircularProgress size={20} color="inherit"/>
                                : 'İçe Aktar'}
                        </Button>
                    </div>

                    {validationError && (
                        <Alert severity="error" style={{marginBottom: 16}}>{validationError}</Alert>
                    )}

                    {!validationError && setupError && (
                        <Alert severity="error" style={{marginBottom: 16}}>
                            {apiErrorMessage}
                        </Alert>
                    )}

                    {isSuccess && isLikelyInvalidHotel && (
                        <Alert severity="error" style={{marginBottom: 16}}>
                            Geçersiz Booking.com Hotel ID veya otel bilgilerine ulaşılamadı.
                            Lütfen ID'yi kontrol edip tekrar deneyiniz.
                        </Alert>
                    )}

                    {isSuccess && !isLikelyInvalidHotel && hasFailures && (
                        <Alert severity="warning" style={{marginBottom: 16}}>
                            Kurulum tamamlandı ancak bazı adımlar başarısız oldu:{' '}
                            <strong>
                                {failedSteps.map(k => stepLabelMap[k] || k).join(', ')}
                            </strong>
                        </Alert>
                    )}

                    {isSuccess && !isLikelyInvalidHotel && hasWarnings && (
                        <Alert severity="warning" style={{marginBottom: 16}}>
                            Bazı bilgiler Booking.com'da bulunamadığı için aktarılamadı:{' '}
                            <strong>
                                {warningSteps.map(k => stepLabelMap[k] || k).join(', ')}
                            </strong>
                            {warningSteps.includes('rooms') && '. Oda tiplerini elle ekleyebilirsiniz.'}
                        </Alert>
                    )}

                    {isSuccess && !isLikelyInvalidHotel && !hasFailures && mode === 'queue' && (
                        <Alert severity="info" style={{marginBottom: 16}}>
                            Bilgiler içe aktarılıyor. İşlem arka planda devam ediyor ve kısa süre içinde tamamlanacaktır.
                        </Alert>
                    )}

                    {isSuccess && !isLikelyInvalidHotel && !hasFailures && !hasWarnings && mode !== 'queue' && (
                        <Alert severity="success" style={{marginBottom: 16}}>
                            Bilgiler başarıyla içe aktarıldı.
                        </Alert>
                    )}

                    {isSuccess && !isLikelyInvalidHotel && result && (
                        <div style={{
                            border: '1px solid #e6e9ef',
                            borderRadius: 8,
                            padding: 16,
                            background: '#f8fafc',
                            marginBottom: 16,
                        }}>
                            {result.details && result.details.data && result.details.data.hotel_name && (
                                <Typography variant="subtitle1" style={{fontWeight: 700, marginBottom: 8}}>
                                    {result.details.data.hotel_name}
                                </Typography>
                            )}
                            {result.photos && result.photos.data && result.photos.data.saved > 0
                                && statRow('Fotoğraflar', `${result.photos.data.saved} adet eklendi`)}
                            {result.facilities && result.facilities.data && result.facilities.data.added > 0
                                && statRow('Olanaklar', `${result.facilities.data.added} adet eklendi`)}
                            {result.rooms && result.rooms.data && result.rooms.data.rooms_created > 0
                                && statRow('Oda Tipleri', `${result.rooms.data.rooms_created} adet oluşturuldu`)}
                            {/* {result.reviews && result.reviews.data
                                && statRow('Yorumlar', `${result.reviews.data.saved || 0} adet`)} */}
                        </div>
                    )}

                    {isSuccess && statusFetching && (
                        <div style={{display: 'flex', alignItems: 'center', gap: 8, color: '#5b6675'}}>
                            <CircularProgress size={16}/>
                            <Typography variant="body2">Kurulum durumu kontrol ediliyor...</Typography>
                        </div>
                    )}

                    {isSuccess && !statusFetching && statusData && statusApiError && (
                        <Alert severity="error" style={{marginBottom: 16}}>
                            Durum kontrolü başarısız: {statusApiErrorMessage || 'Bilinmeyen hata'}
                        </Alert>
                    )}

                    {isSuccess && !statusFetching && statusData && !statusApiError && statusData.data && (
                        <Typography variant="body2" style={{color: '#5b6675'}}>
                            Kurulum durumu:{' '}
                            <strong style={{color: statusColor}}>
                                {statusLabel}
                            </strong>
                            {statusData.data.message && statusValue !== 'completed' && (
                                <span style={{color: '#9ca3af', marginLeft: 8}}>
                                    — {statusData.data.message}
                                </span>
                            )}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {isComplete && !isLikelyInvalidHotel && (
                        <Button
                            onClick={handleCloseAndReload}
                            disabled={isBusy}
                            variant="contained"
                            color="primary"
                            style={{textTransform: 'none', fontWeight: 600, height: 40}}
                        >
                            Kapat
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}

const mapStateToProps = (state) => ({
    userEmail: state.profileReducer && state.profileReducer.profileData
        && state.profileReducer.profileData.profile
        && state.profileReducer.profileData.profile.email,
    fetching: state.bookingImportReducer && state.bookingImportReducer.fetching,
    setupData: state.bookingImportReducer && state.bookingImportReducer.setupData,
    setupError: state.bookingImportReducer && state.bookingImportReducer.setupError,
    statusFetching: state.bookingImportReducer && state.bookingImportReducer.statusFetching,
    statusData: state.bookingImportReducer && state.bookingImportReducer.statusData,
    checkData: state.bookingImportReducer && state.bookingImportReducer.checkData,
});

const mapDispatchToProps = {
    getProfile,
    bookingImportSetup,
    bookingImportStatus,
    bookingImportCheck,
    clearBookingImport,
};

export default connect(mapStateToProps, mapDispatchToProps)(BookingInfoImport);
